/**
 * Gooey Text — morphing headline (Savor-native GooeyText).
 * Faithful port of the 21st.dev component: two overlapping spans cross-blur between phrases
 * under an SVG threshold filter, driven by requestAnimationFrame. Pure JS -> runs everywhere.
 * Reduced motion shows the first phrase static.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class GooeyTextComponent extends Component {
  requiredRefs = ['text1', 'text2'];
  #raf = null;

  connectedCallback() {
    super.connectedCallback();

    const phrases = Array.from(this.querySelectorAll('[data-phrase]'))
      .map((el) => el.textContent.trim())
      .filter(Boolean);

    const { text1, text2 } = this.refs;
    if (phrases.length === 0) return;

    if (phrases.length === 1 || prefersReducedMotion()) {
      text1.textContent = phrases[0];
      text1.style.opacity = '100%';
      text2.style.opacity = '0%';
      return;
    }

    const morphTime = Number(this.dataset.morphTime) || 1;
    const cooldownTime = Number(this.dataset.cooldown) || 0.25;

    let textIndex = phrases.length - 1;
    let time = new Date();
    let morph = 0;
    let cooldown = cooldownTime;

    text1.textContent = phrases[textIndex % phrases.length];
    text2.textContent = phrases[(textIndex + 1) % phrases.length];

    const setMorph = (fraction) => {
      text2.style.filter = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
      text2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;
      const inv = 1 - fraction;
      text1.style.filter = `blur(${Math.min(8 / inv - 8, 100)}px)`;
      text1.style.opacity = `${Math.pow(inv, 0.4) * 100}%`;
    };

    const doCooldown = () => {
      morph = 0;
      text2.style.filter = '';
      text2.style.opacity = '100%';
      text1.style.filter = '';
      text1.style.opacity = '0%';
    };

    const doMorph = () => {
      morph -= cooldown;
      cooldown = 0;
      let fraction = morph / morphTime;
      if (fraction > 1) { cooldown = cooldownTime; fraction = 1; }
      setMorph(fraction);
    };

    const animate = () => {
      this.#raf = requestAnimationFrame(animate);
      const newTime = new Date();
      const shouldIncrement = cooldown > 0;
      const dt = (newTime - time) / 1000;
      time = newTime;
      cooldown -= dt;
      if (cooldown <= 0) {
        if (shouldIncrement) {
          textIndex = (textIndex + 1) % phrases.length;
          text1.textContent = phrases[textIndex % phrases.length];
          text2.textContent = phrases[(textIndex + 1) % phrases.length];
        }
        doMorph();
      } else {
        doCooldown();
      }
    };
    animate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }
}

if (!customElements.get('gooey-text-component')) {
  customElements.define('gooey-text-component', GooeyTextComponent);
}
